import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Clock, Lock, Sparkles } from "lucide-react";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PublicLayout } from "../../components/PublicLayout";
import { RedPacketEnvelopeGrid } from "../../components/red-packet/RedPacketEnvelopeGrid";
import { RedPacketOpenModal } from "../../components/red-packet/RedPacketOpenModal";
import { RedPacketResultModal } from "../../components/red-packet/RedPacketResultModal";
import { StatusTag, type StatusTone } from "../../components/StatusTag";
import { api } from "../../lib/api";
import { money, shortDate } from "../../lib/format";

interface RewardSummary {
  id?: number;
  reward_status: string;
  amount: number;
}

interface Summary {
  member: null | { status: string; status_label: string; identity_code?: string };
  step1_reward?: null | RewardSummary;
  step2_reward?: null | RewardSummary;
  step3_reward?: null | RewardSummary;
  step4_reward?: null | RewardSummary;
  step5_reward?: null | RewardSummary;
  step6_reward?: null | RewardSummary;
  step7_final10_reward?: null | RewardSummary;
  step7_graduation_reward?: null | RewardSummary;
  newbie_progress?: null | {
    step2_status?: string;
    step3_status?: string;
    step4_status?: string;
    step4_unlocked_at?: string | null;
    step4_completed_at?: string | null;
    step5_status?: string;
    step5_deposit_id?: number | null;
    step5_reward_id?: number | null;
    step5_completed_at?: string | null;
    step6_status?: string;
    step6_unlocked_at?: string | null;
    step6_voucher_id?: number | null;
    step6_draw_id?: string | null;
    step6_draw_record_id?: number | null;
    step6_draw_ref?: string | null;
    step6_reward_id?: number | null;
    step6_completed_at?: string | null;
    step6_gem_granted_at?: string | null;
    step7_available_at?: string | null;
    step7_status?: string;
    step7_unlocked_at?: string | null;
    step7_final10_reward_id?: number | null;
    step7_graduation_reward_id?: number | null;
    step7_glory_granted_at?: string | null;
    step7_completed_at?: string | null;
    invite_unlocked_at?: string | null;
  };
  invite_status?: null | { is_unlocked: boolean; unlocked_at?: string | null };
}

interface QuestStep {
  no: string;
  title: string;
  description: string;
  label: string;
  tone: StatusTone;
  featured?: boolean;
}

interface Step4QuizResult {
  passed: boolean;
  locked?: boolean;
  duplicate?: boolean;
  official_unlocked_at?: string;
  hints: string[];
  reward?: null | RewardSummary;
}

type Step6RedPacketResult = {
  ok?: boolean;
  mode?: string;
  status?: string;
  voucher_used?: boolean;
  reward_id?: number | null;
  task_id?: number | null;
  amount?: number | null;
  message?: string;
  error?: string;
};

type NormalizedStep6Result = {
  kind: "success" | "pending_review" | "blocked" | "already_opened" | "error";
  status: string;
  message: string;
  shouldReloadSummary: boolean;
};

type Step6PreviewPhase = "idle" | "selected" | "opening" | "waiting_api" | "result";

type Step6RedPacketRequestResult = {
  raw: Step6RedPacketResult;
  normalized: NormalizedStep6Result;
};

type Step6ModalResult = {
  kind: NormalizedStep6Result["kind"];
  status: string;
  title: string;
  message: string;
  amount?: number | null;
  tone: "info" | "reward" | "error";
};

const pendingMedalStyle: CSSProperties = {
  background: "linear-gradient(160deg, rgba(73, 45, 113, .46), rgba(33, 20, 62, .74))",
  borderColor: "rgba(205, 166, 255, .22)",
  boxShadow: "none",
  color: "rgba(255, 230, 180, .66)"
};

export function NewbiePage() {
  const searchParams = new URLSearchParams(window.location.search);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<Step4QuizResult | null>(null);
  const [quizError, setQuizError] = useState("");
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [step4QuizOpen, setStep4QuizOpen] = useState(false);
  const [step6Opening, setStep6Opening] = useState(false);
  const [step6ConfirmOpen, setStep6ConfirmOpen] = useState(false);
  const [step6Message, setStep6Message] = useState("");
  const [step6Error, setStep6Error] = useState("");
  const [step6PreviewPickerOpen, setStep6PreviewPickerOpen] = useState(false);
  const [step6PreviewSelectedIndex, setStep6PreviewSelectedIndex] = useState<number | null>(null);
  const [step6PreviewPhase, setStep6PreviewPhase] = useState<Step6PreviewPhase>("idle");
  const [step6PreviewResult, setStep6PreviewResult] = useState<Step6ModalResult | null>(null);
  const [step6PreviewApiInFlight, setStep6PreviewApiInFlight] = useState(false);
  const [step6PreviewSubmitted, setStep6PreviewSubmitted] = useState(false);
  const step6PreviewApiInFlightRef = useRef(false);
  const step6PreviewSubmittedRef = useRef(false);
  const queryAccount = searchParams.get("account") ?? searchParams.get("member_account") ?? "";
  const queryIdentityCode = searchParams.get("identity_code") ?? searchParams.get("phone_last3") ?? "";
  const account = queryAccount.trim() || localStorage.getItem("current_member_account") || localStorage.getItem("memberAccount") || "";
  const identityCode = queryIdentityCode.trim() || summary?.member?.identity_code || "";

  async function loadSummary() {
    if (!account) return;
    setSummary(await api<Summary>(`/api/member/summary?member_account=${encodeURIComponent(account)}`));
  }

  useEffect(() => {
    if (queryAccount.trim()) {
      localStorage.setItem("current_member_account", queryAccount.trim());
      localStorage.setItem("memberAccount", queryAccount.trim());
    }
    if (queryIdentityCode.trim()) {
      localStorage.setItem("current_member_identity_code", queryIdentityCode.trim());
      localStorage.setItem("memberIdentityCode", queryIdentityCode.trim());
    }
    setStep6Message("");
    setStep6Error("");
    loadSummary().catch(() => undefined);
  }, [account, queryAccount, queryIdentityCode]);

  const inviteUnlocked = Boolean(summary?.invite_status?.is_unlocked || summary?.newbie_progress?.invite_unlocked_at);
  const steps = useMemo(() => buildSteps(summary), [summary]);
  const vipSpecialReady = useMemo(() => isVipSpecialReady(summary), [summary]);
  const completedCount = steps.filter(isStepCompleted).length;
  const nextStep = steps.find((step) => step.featured) ?? steps.find((step) => step.tone !== "done");
  const heroStepLabel = vipSpecialReady ? "專屬新人禮待審核" : nextStep?.label;
  const heroStepDescription = vipSpecialReady ? "等待客服確認後入帳。" : nextStep?.description;
  const canOpenStep6 = canOpenStep6RedPacket(summary);
  const step6AnimPreviewEnabled = isStep6RedPacketAnimPreviewEnabled();
  const showStep6RedPacketPreview = canOpenStep6 && step6AnimPreviewEnabled;

  useEffect(() => {
    setStep6PreviewPickerOpen(false);
    setStep6PreviewSelectedIndex(null);
    setStep6PreviewPhase("idle");
    setStep6PreviewResult(null);
    setStep6PreviewApiInFlight(false);
    setStep6PreviewSubmitted(false);
    step6PreviewApiInFlightRef.current = false;
    step6PreviewSubmittedRef.current = false;
  }, [account]);

  useEffect(() => {
    if (!showStep6RedPacketPreview && step6PreviewPhase !== "result") {
      setStep6PreviewPickerOpen(false);
      setStep6PreviewSelectedIndex(null);
      setStep6PreviewPhase("idle");
      setStep6PreviewResult(null);
      setStep6PreviewApiInFlight(false);
      setStep6PreviewSubmitted(false);
      step6PreviewApiInFlightRef.current = false;
      step6PreviewSubmittedRef.current = false;
    }
  }, [showStep6RedPacketPreview, step6PreviewPhase]);

  useEffect(() => {
    if (step6PreviewPhase !== "selected" && step6PreviewPhase !== "opening") return undefined;

    const timer = window.setTimeout(() => {
      setStep6PreviewPhase((current) => {
        if (current === "selected") return "opening";
        if (current === "opening") return "waiting_api";
        return current;
      });
    }, step6PreviewPhase === "selected" ? 220 : 980);

    return () => window.clearTimeout(timer);
  }, [step6PreviewPhase]);

  useEffect(() => {
    if (step6PreviewPhase !== "waiting_api") return;
    submitStep6PreviewApi().catch(() => undefined);
  }, [step6PreviewPhase]);

  function openStep6PreviewPicker() {
    if (!showStep6RedPacketPreview || step6PreviewSubmitted || step6PreviewApiInFlight) return;
    setStep6Error("");
    setStep6Message("");
    setStep6PreviewSelectedIndex(null);
    setStep6PreviewPhase("idle");
    setStep6PreviewResult(null);
    setStep6PreviewPickerOpen(true);
  }

  function closeStep6PreviewPicker() {
    if (step6PreviewPhase !== "idle" || step6PreviewApiInFlight) return;
    setStep6PreviewPickerOpen(false);
    setStep6PreviewSelectedIndex(null);
    setStep6PreviewPhase("idle");
  }

  function selectStep6PreviewEnvelope(index: number) {
    if (
      !showStep6RedPacketPreview ||
      !step6PreviewPickerOpen ||
      step6PreviewPhase !== "idle" ||
      step6PreviewSelectedIndex !== null ||
      step6PreviewSubmitted ||
      step6PreviewApiInFlight
    ) {
      return;
    }
    setStep6Error("");
    setStep6Message("");
    setStep6PreviewSelectedIndex(index);
    setStep6PreviewPhase("selected");
  }

  function resetStep6Preview() {
    setStep6PreviewPickerOpen(false);
    setStep6PreviewSelectedIndex(null);
    setStep6PreviewPhase("idle");
    setStep6PreviewResult(null);
  }

  async function requestStep6RedPacket(): Promise<Step6RedPacketRequestResult> {
    const raw = await api<Step6RedPacketResult>("/api/public/newbie/step6-red-packet", {
      method: "POST",
      body: JSON.stringify({
        account,
        identity_code: identityCode
      })
    });
    return {
      raw,
      normalized: normalizeStep6RedPacketResult(raw)
    };
  }

  function applyStep6ResultFeedback(result: NormalizedStep6Result) {
    if (result.kind === "success" || result.kind === "pending_review" || result.kind === "already_opened") {
      setStep6Message(result.message);
      setStep6Error("");
    } else {
      setStep6Error(result.message);
      setStep6Message("");
    }
  }

  async function submitStep6PreviewApi() {
    if (!showStep6RedPacketPreview || step6PreviewApiInFlightRef.current || step6PreviewSubmittedRef.current) return;
    step6PreviewApiInFlightRef.current = true;
    step6PreviewSubmittedRef.current = true;
    setStep6PreviewApiInFlight(true);
    setStep6PreviewSubmitted(true);
    setStep6Error("");
    setStep6Message("");

    try {
      const { raw, normalized } = await requestStep6RedPacket();
      applyStep6ResultFeedback(normalized);
      setStep6PreviewResult(buildStep6ModalResult(raw, normalized));
      setStep6PreviewPhase("result");
      if (shouldReloadStep6ModalSummary(normalized)) await loadSummary();
    } catch (error) {
      const message = error instanceof Error ? error.message : "第 6 關紅包開啟失敗。";
      if (isStep6AlreadyOpenedError(message)) {
        const alreadyOpenedResult = buildStep6AlreadyOpenedModalResult();
        setStep6PreviewResult(alreadyOpenedResult);
        setStep6Message(alreadyOpenedResult.message);
        setStep6Error("");
        setStep6PreviewPhase("result");
        await loadSummary();
      } else {
        const errorResult = buildStep6ErrorModalResult(message);
        setStep6PreviewResult(errorResult);
        setStep6Error(errorResult.message);
        setStep6Message("");
        setStep6PreviewPhase("result");
      }
    } finally {
      step6PreviewApiInFlightRef.current = false;
      setStep6PreviewApiInFlight(false);
    }
  }

  async function submitStep4Quiz() {
    if (!account) return;
    setSubmittingQuiz(true);
    setQuizError("");
    setQuizResult(null);
    try {
      const data = await api<Step4QuizResult>("/api/public/newbie/step4-quiz", {
        method: "POST",
        body: JSON.stringify({
          account,
          answers: step4Questions.map((question) => answers[question.id] ?? "")
        })
      });
      setQuizResult(data);
      if (data.passed) await loadSummary();
    } catch (err) {
      setQuizError(err instanceof Error ? err.message : "第 4 關問答送出失敗。");
    } finally {
      setSubmittingQuiz(false);
    }
  }

  async function openStep6RedPacket() {
    if (!account || step6Opening) return;
    setStep6Opening(true);
    setStep6Error("");
    setStep6Message("");
    try {
      const { normalized: result } = await requestStep6RedPacket();
      applyStep6ResultFeedback(result);
      if (result.shouldReloadSummary) await loadSummary();
    } catch (error) {
      const message = error instanceof Error ? error.message : "第 6 關紅包開啟失敗。";
      if (isStep6AlreadyOpenedError(message)) {
        setStep6Message("你的第 6 關紅包已開啟，請查看獎勵紀錄。");
        setStep6Error("");
        await loadSummary();
      } else {
        setStep6Error(message);
      }
    } finally {
      setStep6Opening(false);
    }
  }

  return (
    <PublicLayout title="新人七關" subtitle="依序完成主線任務，派發完成後才會亮燈。">
      <section className="newbie-map-hero">
        <span className="hero-kicker">新人七關</span>
        <h1>新人七關主線</h1>
        <div className="newbie-progress-row">
          <strong>{completedCount} / 7</strong>
          <span>{heroStepLabel ?? "主線已完成"}</span>
        </div>
        <p>下一步：{heroStepDescription ?? "已完成新人七關。"}</p>
      </section>

      <section className="quest-map">
        {steps.map((step, index) => {
          const stepCompleted = isStepCompleted(step);
          return (
          <article className={`quest-map-node ${step.tone} ${step.featured ? "featured" : ""}`} key={step.no}>
            <div className="map-node-medal" style={stepCompleted ? undefined : pendingMedalStyle}>
              {stepCompleted ? <CheckCircle2 size={20} /> : step.tone === "locked" ? <Lock size={20} /> : <Clock size={20} />}
            </div>
            <div className="map-node-body">
              <div className="quest-head">
                <span>{step.no}</span>
                <StatusTag label={step.label} tone={step.tone} />
              </div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              {step.no === "第 2 關" && step.tone !== "locked" ? (
                <a className="secondary-cta task-guide-link" href="/group-checkin">
                  前往 LINE 群打卡 <ArrowRight size={15} />
                </a>
              ) : null}
              {step.no === "第 4 關" && step.tone === "pending" ? (
                <button className="primary-cta" type="button" onClick={() => setStep4QuizOpen(true)}>
                  開始入盟試煉 <ArrowRight size={15} />
                </button>
              ) : null}
              {step.no === "第 6 關" && canOpenStep6 ? (
                showStep6RedPacketPreview ? (
                  <button
                    className="primary-cta"
                    type="button"
                    onClick={openStep6PreviewPicker}
                    disabled={step6PreviewSubmitted || step6PreviewApiInFlight}
                  >
                    {step6PreviewApiInFlight
                      ? "紅包結果確認中..."
                      : step6PreviewSubmitted
                        ? "紅包狀態已確認"
                        : "選紅包開啟"}{" "}
                    <ArrowRight size={15} />
                  </button>
                ) : (
                  <button className="primary-cta" type="button" onClick={() => setStep6ConfirmOpen(true)} disabled={step6Opening}>
                    {step6Opening ? "開啟中..." : "開啟新人啟航紅包"} <ArrowRight size={15} />
                  </button>
                )
              ) : null}
              {step.no === "第 6 關" && step6Message ? <p className="result">{step6Message}</p> : null}
              {step.no === "第 6 關" && step6Error ? <p className="error-box">{step6Error}</p> : null}
            </div>
            {index < steps.length - 1 ? <i className="route-line" /> : null}
            {step.no === "第 3 關" && inviteUnlocked ? (
              <div className="side-branch-card">
                <Sparkles size={18} />
                <div>
                  <h3>邀友支線已開啟</h3>
                  <p>第 3 關完成後，邀友支線解鎖。好友首存達標後可產生邀友獎勵。</p>
                  <a className="secondary-cta" href="/invite">
                    查看邀友 <ArrowRight size={15} />
                  </a>
                </div>
              </div>
            ) : null}
          </article>
          );
        })}
      </section>

      {step4QuizOpen || shouldShowStep4Quiz(summary) ? (
        <Step4QuizPanel
          summary={summary}
          answers={answers}
          quizResult={quizResult}
          quizError={quizError}
          submitting={submittingQuiz}
          onAnswer={(id, value) => setAnswers((current) => ({ ...current, [id]: value }))}
          onSubmit={submitStep4Quiz}
        />
      ) : null}
      <ConfirmDialog
        open={step6ConfirmOpen}
        title="確認開啟第 6 關紅包？"
        message="開啟後會建立第 6 關紅包獎勵紀錄，請等待客服確認入帳。同一關卡紅包不可重複開啟。"
        confirmText="確認開啟"
        cancelText="取消"
        loading={step6Opening}
        onConfirm={async () => {
          await openStep6RedPacket();
          setStep6ConfirmOpen(false);
        }}
        onCancel={() => {
          if (!step6Opening) setStep6ConfirmOpen(false);
        }}
      />
      {showStep6RedPacketPreview &&
      step6PreviewPickerOpen &&
      step6PreviewPhase !== "opening" &&
      step6PreviewPhase !== "waiting_api" &&
      step6PreviewPhase !== "result" ? (
        <div className="game-modal-backdrop rg-redpacket-picker-backdrop">
          <section className="game-modal rg-redpacket-picker-modal" aria-labelledby="step6-redpacket-picker-title">
            <button className="modal-x" type="button" onClick={closeStep6PreviewPicker} aria-label="關閉紅包預覽">
              ×
            </button>
            <div className="rg-redpacket-picker-head">
              <span className="rg-redpacket-picker-kicker">新人第 6 關</span>
              <h2 id="step6-redpacket-picker-title">選一個紅包開啟</h2>
              <p>紅包結果由系統抽獎決定，選哪個紅包不會影響中獎機率。</p>
            </div>
            <RedPacketEnvelopeGrid
              count={6}
              selectedIndex={step6PreviewSelectedIndex}
              disabled={step6PreviewPhase !== "idle" || step6PreviewApiInFlight || step6PreviewSubmitted}
              variant="newbie-step6"
              onSelect={selectStep6PreviewEnvelope}
            />
            <div className="rg-redpacket-picker-actions">
              <button
                className="secondary-cta"
                type="button"
                onClick={closeStep6PreviewPicker}
                disabled={step6PreviewPhase !== "idle" || step6PreviewApiInFlight || step6PreviewSubmitted}
              >
                先不要
              </button>
            </div>
          </section>
        </div>
      ) : null}
      <RedPacketOpenModal
        open={step6PreviewPhase === "opening" || step6PreviewPhase === "waiting_api"}
        selectedIndex={step6PreviewSelectedIndex}
        variant="newbie-step6"
        message={step6PreviewPhase === "waiting_api" ? "正在確認紅包結果..." : "紅包開啟動畫中..."}
      />
      <RedPacketResultModal
        open={step6PreviewPhase === "result" && Boolean(step6PreviewResult)}
        selectedIndex={step6PreviewSelectedIndex}
        variant="newbie-step6"
        title={step6PreviewResult?.title ?? "紅包狀態確認中"}
        message={step6PreviewResult?.message ?? "紅包狀態確認中，請稍後再試或聯繫客服。"}
        amount={step6PreviewResult?.amount ?? null}
        tone={step6PreviewResult?.tone ?? "info"}
        onClose={resetStep6Preview}
      />
    </PublicLayout>
  );
}

function isStepCompleted(step: QuestStep) {
  return step.tone === "done";
}

function isStep6RedPacketAnimPreviewEnabled() {
  try {
    return localStorage.getItem("rg_step6_red_packet_anim_preview") === "1";
  } catch {
    return false;
  }
}

function canOpenStep6RedPacket(summary: Summary | null) {
  if (!summary) return false;
  const step5Done =
    summary.step5_reward?.reward_status === "completed" ||
    summary.newbie_progress?.step5_status === "completed";
  const step6Done =
    summary.step6_reward?.reward_status === "completed" ||
    summary.newbie_progress?.step6_status === "completed";
  const step6Ready =
    summary.step6_reward?.reward_status === "ready" ||
    summary.newbie_progress?.step6_status === "ready" ||
    summary.newbie_progress?.step6_status === "waiting_dispatch";
  const step6UnlockedAt = summary.newbie_progress?.step6_unlocked_at;
  const step6WaitingNoon =
    Boolean(step6UnlockedAt) && Date.now() < new Date(step6UnlockedAt ?? "").getTime();
  return Boolean(step5Done && summary.newbie_progress?.step6_voucher_id && !step6Done && !step6Ready && !step6WaitingNoon);
}

function shouldReloadStep6ModalSummary(result: NormalizedStep6Result) {
  return result.shouldReloadSummary || result.kind === "blocked";
}

function buildStep6ModalResult(data: Step6RedPacketResult, result: NormalizedStep6Result): Step6ModalResult {
  const amount = result.kind === "success" && typeof data.amount === "number" ? data.amount : null;

  if (result.kind === "success" && result.status === "task_enqueued") {
    return {
      kind: "success",
      status: result.status,
      title: "紅包已開啟",
      message: amount !== null ? "獎勵派發中。" : "紅包已開啟，獎勵派發中。",
      amount,
      tone: "reward"
    };
  }

  if (result.kind === "success" && result.status === "reward_created") {
    return {
      kind: "success",
      status: result.status,
      title: "紅包已開啟",
      message: amount !== null ? "獎勵已建立，等待派發。" : "紅包已開啟，獎勵已建立，等待派發。",
      amount,
      tone: "reward"
    };
  }

  if (result.kind === "pending_review" && result.status === "pending_review") {
    return {
      kind: "pending_review",
      status: result.status,
      title: "等待客服確認",
      message: "紅包已開啟，獎項需客服確認，請稍候。",
      amount: null,
      tone: "info"
    };
  }

  if (result.status === "reservation_missing") {
    return {
      kind: "blocked",
      status: result.status,
      title: "紅包資格確認中",
      message: "紅包資格確認中，券不會被消耗，請稍後再試或聯繫客服。",
      amount: null,
      tone: "info"
    };
  }

  if (result.status === "no_available_items") {
    return {
      kind: "blocked",
      status: result.status,
      title: "紅包活動調整中",
      message: "目前紅包活動調整中，券不會被消耗，請稍後再試。",
      amount: null,
      tone: "info"
    };
  }

  if (result.status === "v2_disabled") {
    return {
      kind: "blocked",
      status: result.status,
      title: "紅包活動升級中",
      message: "紅包活動升級中，券不會被消耗，請稍後再試。",
      amount: null,
      tone: "info"
    };
  }

  if (result.status === "v2_identity_present") {
    return {
      kind: "blocked",
      status: result.status,
      title: "紅包券狀態需確認",
      message: "紅包券狀態需新版流程確認，券不會被消耗，請聯繫客服。",
      amount: null,
      tone: "info"
    };
  }

  if (result.status === "already_opened" || result.status === "already_drawn") {
    return buildStep6AlreadyOpenedModalResult();
  }

  if (result.status === "needs_review") {
    return {
      kind: "blocked",
      status: result.status,
      title: "紅包狀態需人工確認",
      message: "紅包狀態需人工確認，請聯繫客服。",
      amount: null,
      tone: "info"
    };
  }

  if (result.status === "quota_exhausted") {
    return {
      kind: "blocked",
      status: result.status,
      title: "紅包額度確認中",
      message: "紅包額度確認中，券不會被消耗，請稍後再試或聯繫客服。",
      amount: null,
      tone: "info"
    };
  }

  return {
    kind: result.kind,
    status: result.status,
    title: result.kind === "error" ? "紅包開啟失敗" : "紅包狀態確認中",
    message: result.kind === "error" ? result.message : "紅包狀態確認中，請稍後再試或聯繫客服。",
    amount: null,
    tone: result.kind === "error" ? "error" : "info"
  };
}

function buildStep6AlreadyOpenedModalResult(): Step6ModalResult {
  return {
    kind: "already_opened",
    status: "already_opened",
    title: "紅包已開啟",
    message: "紅包已開啟，獎勵派發處理中，請查看獎勵紀錄。",
    amount: null,
    tone: "info"
  };
}

function buildStep6ErrorModalResult(message: string): Step6ModalResult {
  return {
    kind: "error",
    status: "error",
    title: "紅包開啟失敗",
    message,
    amount: null,
    tone: "error"
  };
}

function normalizeStep6RedPacketResult(data: Step6RedPacketResult | undefined | null): NormalizedStep6Result {
  if (!data) {
    return {
      kind: "error",
      status: "missing_response",
      message: "紅包狀態無法確認，請聯繫客服。",
      shouldReloadSummary: false
    };
  }

  const status = data.status ?? (data.ok === false ? data.error : undefined) ?? "unknown";
  const forcedBlocked = data.ok === false || data.mode === "blocked";

  if (forcedBlocked) {
    if (status === "already_drawn" || status === "already_opened") {
      return {
        kind: "already_opened",
        status,
        message: "你的第 6 關紅包已開啟，請查看獎勵紀錄。",
        shouldReloadSummary: true
      };
    }

    if (status === "reservation_missing") {
      return {
        kind: "blocked",
        status,
        message: "紅包資格確認中，券不會被消耗，請稍後再試或聯繫客服。",
        shouldReloadSummary: false
      };
    }

    if (status === "no_available_items") {
      return {
        kind: "blocked",
        status,
        message: "紅包活動目前調整中，券不會被消耗，請稍後再試。",
        shouldReloadSummary: false
      };
    }

    if (status === "v2_disabled") {
      return {
        kind: "blocked",
        status,
        message: "紅包活動升級中，請稍後再試，券不會被消耗。",
        shouldReloadSummary: false
      };
    }

    if (status === "v2_identity_present") {
      return {
        kind: "blocked",
        status,
        message: "紅包券狀態需新版流程確認，券不會被消耗，請聯繫客服。",
        shouldReloadSummary: false
      };
    }

    if (status === "quota_exhausted") {
      return {
        kind: "blocked",
        status,
        message: "活動額度目前已達限制，券不會被消耗，請聯繫客服或稍後再試。",
        shouldReloadSummary: false
      };
    }

    if (status === "needs_review") {
      return {
        kind: "blocked",
        status,
        message: "派發狀態需要人工確認，請聯繫客服。",
        shouldReloadSummary: false
      };
    }

    return {
      kind: "blocked",
      status,
      message: "紅包狀態需人工確認，請聯繫客服。",
      shouldReloadSummary: false
    };
  }

  if (status === "task_enqueued") {
    return {
      kind: "success",
      status,
      message: "紅包已開啟，獎勵派發處理中，請稍候查看獎勵紀錄。",
      shouldReloadSummary: true
    };
  }

  if (status === "reward_created") {
    return {
      kind: "success",
      status,
      message: "紅包已開啟，獎勵已建立，等待派發處理。",
      shouldReloadSummary: true
    };
  }

  if (status === "pending_review") {
    return {
      kind: "pending_review",
      status,
      message: "紅包已開啟，獎項需客服確認，請稍候。",
      shouldReloadSummary: true
    };
  }

  if (status === "already_drawn" || status === "already_opened") {
    return {
      kind: "already_opened",
      status,
      message: "你的第 6 關紅包已開啟，請查看獎勵紀錄。",
      shouldReloadSummary: true
    };
  }

  if (status === "reservation_missing") {
    return {
      kind: "blocked",
      status,
      message: "紅包資格確認中，券不會被消耗，請稍後再試或聯繫客服。",
      shouldReloadSummary: false
    };
  }

  if (status === "no_available_items") {
    return {
      kind: "blocked",
      status,
      message: "紅包活動目前調整中，券不會被消耗，請稍後再試。",
      shouldReloadSummary: false
    };
  }

  if (status === "v2_disabled") {
    return {
      kind: "blocked",
      status,
      message: "紅包活動升級中，請稍後再試，券不會被消耗。",
      shouldReloadSummary: false
    };
  }

  if (status === "v2_identity_present") {
    return {
      kind: "blocked",
      status,
      message: "紅包券狀態需新版流程確認，券不會被消耗，請聯繫客服。",
      shouldReloadSummary: false
    };
  }

  if (status === "quota_exhausted") {
    return {
      kind: "blocked",
      status,
      message: "活動額度目前已達限制，券不會被消耗，請聯繫客服或稍後再試。",
      shouldReloadSummary: false
    };
  }

  if (status === "needs_review") {
    return {
      kind: "blocked",
      status,
      message: "派發狀態需要人工確認，請聯繫客服。",
      shouldReloadSummary: false
    };
  }

  return {
    kind: "error",
    status,
    message: "紅包狀態需人工確認，請聯繫客服。",
    shouldReloadSummary: false
  };
}

function isStep6AlreadyOpenedError(message: string) {
  return message.includes("第 6 關紅包已開啟") || message.includes("第6關紅包已開啟") || message.includes("此紅包已開啟");
}

function buildSteps(summary: Summary | null): QuestStep[] {
  const step1Status = summary?.step1_reward?.reward_status;
  const step2Status = summary?.step2_reward?.reward_status;
  const step3Status = summary?.step3_reward?.reward_status;
  const step4Status = summary?.step4_reward?.reward_status;
  const step5Status = summary?.step5_reward?.reward_status;
  const step6Status = summary?.step6_reward?.reward_status;

  const step1Done = step1Status === "completed";
  const step2Done = step2Status === "completed";
  const step3Done = step3Status === "completed";
  const step4Done = step4Status === "completed" || summary?.newbie_progress?.step4_status === "completed";
  const step4Ready = step4Status === "ready";
  const step5Done = step5Status === "completed" || summary?.newbie_progress?.step5_status === "completed";
  const step5Ready = step5Status === "ready" || summary?.newbie_progress?.step5_status === "ready";
  const step6Done = step6Status === "completed" || summary?.newbie_progress?.step6_status === "completed";
  const step6Ready = step6Status === "ready" || summary?.newbie_progress?.step6_status === "ready" || summary?.newbie_progress?.step6_status === "waiting_dispatch";
  const step6UnlockedAt = summary?.newbie_progress?.step6_unlocked_at;
  const step6WaitingNoon = step5Done && !step6Done && !step6Ready && Boolean(step6UnlockedAt) && Date.now() < new Date(step6UnlockedAt ?? "").getTime();
  const step6HasVoucher = Boolean(summary?.newbie_progress?.step6_voucher_id);
  const step7FinalDone = summary?.step7_final10_reward?.reward_status === "completed";
  const step7GiftDone = summary?.step7_graduation_reward?.reward_status === "completed";
  const step7RewardCreated = Boolean(summary?.step7_final10_reward || summary?.step7_graduation_reward || summary?.newbie_progress?.step7_status === "ready");
  const step7Dispatching = step7RewardCreated && !(step7FinalDone && step7GiftDone);
  const step7AvailableAt = summary?.newbie_progress?.step7_available_at;
  const step7WaitingNoon = step6Done && !step7RewardCreated && Boolean(step7AvailableAt) && Date.now() < new Date(step7AvailableAt ?? "").getTime();
  const step7Done = summary?.newbie_progress?.step7_status === "completed" || summary?.member?.status === "graduated" || (step7FinalDone && step7GiftDone);
  if (isVipSpecialReady(summary)) return buildVipSpecialReadySteps(summary);

  return [
    {
      no: "第 1 關",
      title: "入盟啟動 70%",
      description: summary?.step1_reward ? `已建立派發 ${money(summary.step1_reward.amount)} 河幣。` : "選定入盟啟動筆後建立第 1 關派發。",
      label: step1Done ? "已領取" : rewardLabel(step1Status),
      tone: rewardTone(step1Status)
    },
    {
      no: "第 2 關",
      title: "回盟補給 10%",
      description: summary?.step2_reward ? `已建立派發 ${money(summary.step2_reward.amount)} 河幣。` : step1Done ? "完成第 1 關後可進行回盟補給。" : "完成第 1 關後開放。",
      label: step2Done ? "已領取" : rewardLabel(step2Status, step1Done ? "可進行" : "鎖定"),
      tone: step2Status ? rewardTone(step2Status) : step1Done ? "pending" : "locked"
    },
    {
      no: "第 3 關",
      title: "二存加碼 80%",
      description: summary?.step3_reward ? `已建立派發 ${money(summary.step3_reward.amount)} 河幣，完成後寶石碎片 +1。` : step2Done ? "第二筆單筆滿 1,000 後可獲得二存加碼。" : "完成第 2 關後開放。",
      label: step3Done ? "已領取" : rewardLabel(step3Status, step2Done ? "可進行" : "鎖定"),
      tone: step3Status ? rewardTone(step3Status) : step2Done ? "pending" : "locked"
    },
    {
      no: "第 4 關",
      title: "入盟試煉 10%",
      description: step4Done
        ? `已完成入盟試煉，派發 ${money(summary?.step4_reward?.amount ?? 0)} 河幣。`
        : step4Ready
          ? `已送入審核流程，等待入帳 ${money(summary?.step4_reward?.amount ?? 0)} 河幣。`
          : step3Done
            ? "完成 4 題問答後建立第 4 關派發。"
            : "第 3 關完成後開放。",
      label: step4Done ? "已領取" : step4Ready ? "等待派發" : step3Done ? "可作答" : "鎖定",
      tone: step4Done ? "done" : step4Ready ? "claim" : step3Done ? "pending" : "locked",
      featured: step3Done && !step4Done && !step4Ready
    },
    {
      no: "第 5 關",
      title: "三存加碼 50%",
      description: step5Done
        ? `已領取三存加碼 50%，派發 ${money(summary?.step5_reward?.amount ?? 0)} 河幣。`
        : step5Ready
          ? `三存加碼已送入審核流程，等待入帳 ${money(summary?.step5_reward?.amount ?? 0)} 河幣。`
          : step4Done
            ? "第三筆單筆滿 1,000 後，可獲得三存加碼 50%。"
            : "第 5 關尚未開放，等待第 4 關完成後才會開啟。",
      label: step5Done ? "已領取" : step5Ready ? "等待派發" : step4Done ? "可進行" : "鎖定 / 尚未開放",
      tone: step5Done ? "done" : step5Ready ? "claim" : step4Done ? "pending" : "locked",
      featured: step4Done && !step5Done && !step5Ready
    },
    {
      no: "第 6 關",
      title: "新人啟航紅包",
      description: step6Done
        ? `第 6 關已完成，紅包獎勵 ${money(summary?.step6_reward?.amount ?? 0)} 河幣，寶石碎片 +1。第 7 關等待隔天 12:00 開啟。`
        : step6Ready
          ? `新人啟航紅包已抽中 ${money(summary?.step6_reward?.amount ?? 0)} 河幣，獎勵派發中。`
          : step6WaitingNoon
            ? "新人啟航紅包將於隔天 12:00 開放。"
            : step5Done && step6HasVoucher
              ? "使用新人啟航紅包券，最高可抽 58,888 河幣。"
              : step5Done
                ? "尚未取得新人啟航紅包券，請先完成第 5 關三存進階。"
                : "第 6 關尚未開啟，完成第 5 關後隔天 12:00 開放。",
      label: step6Done ? "已完成" : step6Ready ? "派發中" : step6WaitingNoon ? "等待 12:00" : step5Done && step6HasVoucher ? "可抽紅包" : step5Done ? "尚未取得紅包券" : "鎖定 / 尚未開放",
      tone: step6Done ? "done" : step6Ready ? "claim" : step5Done ? "pending" : "locked",
      featured: step5Done && !step6Done && !step6Ready
    },
    {
      no: "第 7 關",
      title: "聚耀畢業",
      description: step7Done
        ? "恭喜完成新人七關，正式成為龍來喬成員。"
        : step7Dispatching
          ? `畢業獎勵派發中｜首存最後 10%：${rewardLabel(summary?.step7_final10_reward?.reward_status, "待派發")}｜畢業禮：${rewardLabel(summary?.step7_graduation_reward?.reward_status, "待派發")}`
          : step7WaitingNoon
            ? "聚耀畢業關卡將於隔天 12:00 開啟。"
            : step6Done
              ? "聚耀畢業關卡已開啟，完成最後加碼與畢業禮派發後，即可升級為正式成員。"
              : "第 7 關尚未開啟，完成第 6 關後隔天 12:00 開放。",
      label: step7Done ? "已畢業 / 已完成" : step7Dispatching ? "畢業獎勵派發中" : step7WaitingNoon ? "等待 12:00" : step6Done ? "可產生畢業派發" : "鎖定 / 尚未開放",
      tone: step7Done ? "done" : step7Dispatching ? "claim" : step6Done ? "pending" : "locked",
      featured: step6Done && !step7Done && !step7Dispatching
    }
  ];
}

function isVipSpecialReady(summary: Summary | null) {
  if (!summary) return false;
  const step6Completed = summary.step6_reward?.reward_status === "completed" || summary.newbie_progress?.step6_status === "completed";
  const step6Created = Boolean(summary.step6_reward || summary.newbie_progress?.step6_reward_id || summary.newbie_progress?.step6_draw_record_id);
  const final10Ready = summary.step7_final10_reward?.reward_status === "ready";
  const graduationGiftCreated = Boolean(summary.step7_graduation_reward || summary.newbie_progress?.step7_graduation_reward_id);
  return Boolean(final10Ready && !graduationGiftCreated && !step6Completed && !step6Created && summary.member?.status !== "graduated");
}

function buildVipSpecialReadySteps(summary: Summary | null): QuestStep[] {
  return [
    vipReadyStep("第 1 關", "入盟啟動 70%", summary?.step1_reward, "新人禮已建立，待審核入帳。"),
    vipReadyStep("第 2 關", "回盟補給 10%", summary?.step2_reward, "回盟補給已建立，待審核入帳。"),
    vipReadyStep("第 3 關", "二存加碼 80%", summary?.step3_reward, "二存加碼已建立，待審核入帳。"),
    vipReadyStep("第 4 關", "入盟試煉 10%", summary?.step4_reward, "入盟試煉獎勵已建立，待審核入帳。"),
    vipReadyStep("第 5 關", "三存加碼 50%", summary?.step5_reward, "三存加碼已建立，待審核入帳。"),
    {
      no: "第 6 關",
      title: "新人啟航紅包",
      description: "第 6 關紅包尚未開放，前面獎勵入帳後開放。",
      label: "鎖定 / 尚未開放",
      tone: "locked"
    },
    {
      no: "第 7 關",
      title: "聚耀畢業",
      description: "首存最後 10% 待審核；畢業禮尚未建立。",
      label: "首存最後 10% 待審核",
      tone: "claim"
    }
  ];
}

function vipReadyStep(no: string, title: string, reward: RewardSummary | null | undefined, fallback: string): QuestStep {
  if (reward?.reward_status === "completed") {
    return {
      no,
      title,
      description: `${title} 已入帳 ${money(reward.amount)} 河幣。`,
      label: "已入帳",
      tone: "done"
    };
  }
  return {
    no,
    title,
    description: reward ? `${fallback} 金額 ${money(reward.amount)} 河幣。` : fallback,
    label: reward ? "待審核" : "尚未開放",
    tone: reward ? "claim" : "locked"
  };
}

function Step4QuizPanel({
  summary,
  answers,
  quizResult,
  quizError,
  submitting,
  onAnswer,
  onSubmit
}: {
  summary: Summary | null;
  answers: Record<string, string>;
  quizResult: Step4QuizResult | null;
  quizError: string;
  submitting: boolean;
  onAnswer: (id: string, value: string) => void;
  onSubmit: () => void;
}) {
  const step3Done = summary?.step3_reward?.reward_status === "completed";
  const step4Done = summary?.step4_reward?.reward_status === "completed" || summary?.newbie_progress?.step4_status === "completed";
  const step4Ready = summary?.step4_reward?.reward_status === "ready";

  if (!step3Done) {
    return (
      <section className="step4-quiz-card locked">
        <h2>第 4 關入盟試煉</h2>
        <p>第 3 關完成後才會開放入盟試煉。</p>
      </section>
    );
  }

  if (step4Done) {
    return (
      <section className="step4-quiz-card completed">
        <h2>第 4 關入盟試煉</h2>
        <p>第 4 關已完成，派發完成後才會亮燈。</p>
      </section>
    );
  }

  if (step4Ready) {
    return (
      <section className="step4-quiz-card ready">
        <h2>第 4 關入盟試煉</h2>
        <p>問答已通過，獎勵已送入審核流程，等待入帳。</p>
      </section>
    );
  }

  return (
    <section className="step4-quiz-card">
      <span className="hero-kicker">第 4 關問答</span>
      <h2>第 4 關入盟試煉</h2>
      <p>完成 4 題問答後才會建立第 4 關派發。答錯會給提示，可重新作答。</p>
      <div className="step4-question-list">
        {step4Questions.map((question, index) => (
          <div className="step4-question" key={question.id}>
            <h3>
              {index + 1}. {question.title}
            </h3>
            <div className="step4-options">
              {question.options.map((option) => (
                <label className={answers[question.id] === option.value ? "selected" : ""} key={option.value}>
                  <input
                    type="radio"
                    name={question.id}
                    checked={answers[question.id] === option.value}
                    onChange={() => onAnswer(question.id, option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      {quizResult?.hints?.length ? (
        <div className="hint-box">
          <strong>提示</strong>
          {quizResult.hints.map((hint) => (
            <p key={hint}>{hint}</p>
          ))}
        </div>
      ) : null}
      {quizResult?.passed ? <p className="result">問答通過，已建立第 4 關派發。</p> : null}
      {quizError ? <p className="error-box">{quizError}</p> : null}
      <button className="primary-cta" onClick={onSubmit} disabled={submitting}>
        {submitting ? "送出中..." : "送出入盟試煉"}
      </button>
    </section>
  );
}

function shouldShowStep4Quiz(summary: Summary | null) {
  const step3Done = summary?.step3_reward?.reward_status === "completed";
  const step4Done = summary?.step4_reward?.reward_status === "completed" || summary?.newbie_progress?.step4_status === "completed";
  const step4Ready = summary?.step4_reward?.reward_status === "ready";
  return step3Done && !step4Done && !step4Ready;
}

function rewardLabel(status?: string, fallback = "尚未開放") {
  if (status === "completed") return "已領取";
  if (status === "ready") return "等待派發";
  if (status === "failed") return "待人工處理";
  if (status === "canceled") return "已取消";
  return fallback;
}

function rewardTone(status?: string): StatusTone {
  if (status === "completed") return "done";
  if (status === "ready") return "claim";
  if (status === "failed") return "pending";
  if (status === "canceled") return "locked";
  return "locked";
}

const step4Questions = [
  {
    id: "favorite_game_type",
    title: "常玩的遊戲類型",
    options: [
      { value: "slots", label: "電子" },
      { value: "board", label: "棋牌" },
      { value: "baccarat", label: "電子百家" }
    ]
  },
  {
    id: "favorite_campaign",
    title: "喜歡的活動",
    options: [
      { value: "red_packet", label: "紅包" },
      { value: "deposit_bonus", label: "儲值加碼" },
      { value: "ranking", label: "排行榜" },
      { value: "quest", label: "闖關" },
      { value: "invite", label: "邀友" }
    ]
  },
  {
    id: "first_deposit_100",
    title: "龍來喬首存 100% 怎麼領",
    options: [
      { value: "split", label: "70% 立即領，30% 任務解鎖" },
      { value: "all_once", label: "全部一次領" },
      { value: "no_task", label: "不用完成任務" }
    ]
  },
  {
    id: "side_branch",
    title: "支線任務是否影響主線",
    options: [
      { value: "extra_bonus", label: "不會，支線是額外加碼" },
      { value: "blocks_main", label: "會影響主線" },
    ]
  }
];
